import * as React from "react";
import { useContext, useState, useMemo } from "react";
import * as Immutable from "immutable";
import styled, { css } from "styled-components";
import MessageFieldsFilter from "logic/message/MessageFieldsFilter";
import FieldType from "views/logic/fieldtypes/FieldType";
import Field from "views/components/Field";
import useAutoRefresh from "views/hooks/useAutoRefresh";
import { TableHeaderCell, TableHead } from "views/components/datatable";
import InteractiveContext from "views/components/contexts/InteractiveContext";
import FieldSortIcon from "./FieldSortIcon";
import MessageTableEntry from "./MessageTableEntry";
import MessageTableProviders from "./MessageTableProviders";
import BulkSelectHead from "./BulkSelectHead";
const Table = styled.table(
  ({ theme }) => css`
    position: relative;
    font-size: ${theme.fonts.size.small};
    margin: 0;
    border-collapse: collapse;
    width: 100%;
    word-break: break-all;

    > tbody > tr > td {
      border-color: ${theme.colors.table.row.divider};
    }

    @media print {
      font-size: ${theme.fonts.size.body};
      padding-left: 0;
      min-width: 50%;

      th {
        font-weight: bold !important;
        font-size: inherit !important;
        white-space: break-spaces !important;
        word-break: break-all !important;
      }

      th,
      td {
        border: 1px ${theme.colors.gray[80]} solid !important;
        left: 0;
        padding: 5px !important;
        position: static;
      }
    }
  `
);
const TableWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    overflow: auto;

    /* Fixes overflow of children with position: fixed */
    clip-path: inset(0 0 0 0);

    @media screen and (max-width: ${theme.breakpoints.max.md}) {
      &.table-responsive {
        overflow-y: auto;
      }
    }
  `
);
const _fieldTypeFor = (fieldName, fields) => (fields && fields.find((f) => f.name === fieldName) || { type: FieldType.Unknown }).type;
const _getFormattedMessages = (messages) => messages.map((m) => ({
  fields: m.message,
  formatted_fields: MessageFieldsFilter.filterFields(m.message),
  id: m.message._id,
  index: m.index,
  highlight_ranges: m.highlight_ranges,
  decoration_stats: m.decoration_stats
}));
const _toggleMessageDetail = (id, expandedMessages, setExpandedMessages, stopAutoRefresh) => {
  let newSet;
  if (expandedMessages.contains(id)) {
    newSet = expandedMessages.delete(id);
  } else {
    newSet = expandedMessages.add(id);
    stopAutoRefresh();
  }
  setExpandedMessages(newSet);
};
const MessageTable = ({
  fields,
  messages,
  config,
  onSortChange = void 0,
  renderRowActions = void 0,
  setLoadingState,
  scrollContainerRef,
  displayBulkSelectCol = false,
  isEntitySelectable = () => false,
  rowOverride = void 0
}) => {
  const { stopAutoRefresh } = useAutoRefresh();
  const [expandedMessages, setExpandedMessages] = useState(Immutable.Set());
  const formattedMessages = useMemo(() => _getFormattedMessages(messages), [messages]);
  const selectedFields = useMemo(() => {
    var _a;
    return Immutable.OrderedSet((_a = config == null ? void 0 : config.fields) != null ? _a : []);
  }, [config == null ? void 0 : config.fields]);
  const interactive = useContext(InteractiveContext);
  const toggleDetail = useMemo(
    () => interactive ? (id) => _toggleMessageDetail(id, expandedMessages, setExpandedMessages, stopAutoRefresh) : () => {
    },
    [expandedMessages, interactive, stopAutoRefresh]
  );
  return /* @__PURE__ */ React.createElement(MessageTableProviders, null, /* @__PURE__ */ React.createElement(TableWrapper, { className: "table-responsive", id: "sticky-augmentations-container", ref: scrollContainerRef }, /* @__PURE__ */ React.createElement(Table, { className: "table table-condensed" }, /* @__PURE__ */ React.createElement(TableHead, null, /* @__PURE__ */ React.createElement("tr", null, displayBulkSelectCol && /* @__PURE__ */ React.createElement(TableHeaderCell, { $noMinWidth: true }, /* @__PURE__ */ React.createElement(BulkSelectHead, null)), selectedFields.toSeq().map((selectedFieldName) => {
    const type = _fieldTypeFor(selectedFieldName, fields);
    const isCompound = type.isCompound();
    return /* @__PURE__ */ React.createElement(TableHeaderCell, { key: selectedFieldName, $isNumeric: type.isNumeric() }, /* @__PURE__ */ React.createElement(Field, { type, name: selectedFieldName }, selectedFieldName), interactive && !isCompound && /* @__PURE__ */ React.createElement(
      FieldSortIcon,
      {
        fieldName: selectedFieldName,
        onSortChange,
        setLoadingState,
        config
      }
    ));
  }).toArray(), renderRowActions && /* @__PURE__ */ React.createElement(TableHeaderCell, null))), formattedMessages.map((message) => {
    const messageKey = `${message.index}-${message.id}`;
    const overrideContent = rowOverride == null ? void 0 : rowOverride(message);
    return /* @__PURE__ */ React.createElement(
      MessageTableEntry,
      {
        fields,
        key: messageKey,
        message,
        config,
        showMessageRow: config == null ? void 0 : config.showMessageRow,
        selectedFields,
        rowActions: renderRowActions == null ? void 0 : renderRowActions(message),
        expanded: expandedMessages.contains(messageKey),
        toggleDetail,
        expandAllRenderAsync: false,
        displayBulkSelectCol,
        isEntitySelectable,
        overrideContent
      }
    );
  }))));
};
export default React.memo(MessageTable);
